import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  CalendarDays,
  ChevronRight,
  Clock3,
  Eye,
  FileClock,
  History,
  Loader2,
  UserRound,
  UsersRound,
  X,
} from 'lucide-react';
import { fetchMetasUlHistory } from './api';
import { MetaUlHistoryEntry, MetaUlHistoryChange } from './types';

type HistoryModalProps = {
  year: string;
  onClose: () => void;
};

function parseEntryData(entry: MetaUlHistoryEntry) {
  if (typeof entry.data !== 'string') return entry.data;

  try {
    return JSON.parse(entry.data) as { year: number; changes: MetaUlHistoryChange[] };
  } catch {
    return { year: 0, changes: [] };
  }
}

function formatApprovedValue(value: number | null) {
  if (value === null || value === undefined) return 'Sem valor';
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value: string) {
  const date = new Date(value);
  return {
    date: date.toLocaleDateString('pt-BR'),
    time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    full: date.toLocaleString('pt-BR'),
  };
}

function getInitials(name: string) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return `${parts[0][0] || ''}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase();
}

export const HistoryModal = ({ year, onClose }: HistoryModalProps) => {
  const [entries, setEntries] = useState<MetaUlHistoryEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<MetaUlHistoryEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadHistory = async () => {
      setIsLoading(true);
      try {
        const history = await fetchMetasUlHistory();
        if (active) setEntries(history);
      } catch {
        if (active) setEntries([]);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadHistory();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedEntry) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedEntry(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntry]);

  const filteredEntries = useMemo(
    () => entries
      .map((entry) => ({ entry, data: parseEntryData(entry) }))
      .filter(({ data }) => String(data.year) === year),
    [entries, year]
  );

  const summary = useMemo(() => ({
    updates: filteredEntries.length,
    changes: filteredEntries.reduce((total, item) => total + (item.data.changes?.length || 0), 0),
    users: new Set(filteredEntries.map(({ entry }) => entry.email || entry.name)).size,
  }), [filteredEntries]);

  const selectedData = selectedEntry ? parseEntryData(selectedEntry) : null;
  const selectedDate = selectedEntry ? formatDate(selectedEntry.created_at) : null;

  return (
    <div className="relative flex max-h-[88vh] min-h-[560px] flex-col overflow-hidden rounded-3xl border border-white/70 bg-slate-50 shadow-2xl">
      <header className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 px-6 py-6 text-white sm:px-8">
        <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-sky-300/20 blur-3xl" />

        <div className="relative flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-inner">
              <History className="h-6 w-6" />
            </div>
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-blue-300/20 bg-blue-400/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-100">
                  Auditoria
                </span>
                <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium text-slate-100">
                  Ano {year}
                </span>
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-white">Hist&oacute;rico de Metas UL</h2>
              <p className="mt-1 max-w-2xl text-sm text-blue-100/80">
                Consulte quem alterou as metas e compare os valores anteriores com os novos.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-white/10 bg-white/10 p-2.5 text-white/80 transition hover:bg-white/20 hover:text-white"
            aria-label="Fechar hist&oacute;rico"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-white px-6 py-4 sm:grid-cols-3 sm:px-8">
        <div className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3">
          <div className="rounded-xl bg-blue-600 p-2 text-white"><FileClock className="h-4 w-4" /></div>
          <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Atualiza&ccedil;&otilde;es</p><p className="text-xl font-bold text-slate-900">{summary.updates}</p></div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3">
          <div className="rounded-xl bg-blue-700 p-2 text-white"><History className="h-4 w-4" /></div>
          <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Campos alterados</p><p className="text-xl font-bold text-slate-900">{summary.changes}</p></div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3">
          <div className="rounded-xl bg-blue-800 p-2 text-white"><UsersRound className="h-4 w-4" /></div>
          <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Usu&aacute;rios</p><p className="text-xl font-bold text-slate-900">{summary.users}</p></div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-8">
        {isLoading ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 text-slate-500">
            <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
              <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
            </div>
            <span className="text-sm font-medium">Carregando hist&oacute;rico...</span>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center px-4 text-center">
            <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <FileClock className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-base font-semibold text-slate-800">Nenhuma altera&ccedil;&atilde;o em {year}</h3>
            <p className="mt-1 max-w-sm text-sm text-slate-500">As pr&oacute;ximas atualiza&ccedil;&otilde;es das metas aparecer&atilde;o aqui.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEntries.map(({ entry, data }) => {
              const entryDate = formatDate(entry.created_at);
              const changeCount = data.changes?.length || 0;

              return (
                <button
                  key={entry.id_log}
                  type="button"
                  onClick={() => setSelectedEntry(entry)}
                  className="group grid w-full grid-cols-1 items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg sm:grid-cols-[150px_minmax(0,1fr)_150px_44px] sm:px-5"
                >
                  <div className="flex items-center gap-3 sm:block">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><CalendarDays className="h-4 w-4 text-blue-600" />{entryDate.date}</div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-500"><Clock3 className="h-3.5 w-3.5" />{entryDate.time}</div>
                  </div>

                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 text-xs font-bold text-white shadow-sm">
                      {getInitials(entry.name)}
                    </div>
                    <div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-900">{entry.name}</p><p className="truncate text-xs text-slate-500">{entry.email}</p></div>
                  </div>

                  <div className="flex sm:justify-center">
                    <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700">
                      <Eye className="h-3.5 w-3.5" />
                      {changeCount} {changeCount === 1 ? 'altera\u00e7\u00e3o' : 'altera\u00e7\u00f5es'}
                    </span>
                  </div>

                  <span className="hidden h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition group-hover:bg-blue-600 group-hover:text-white sm:flex">
                    <ChevronRight className="h-5 w-5" />
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selectedEntry && selectedData && selectedDate ? (
        <div
          className="absolute inset-0 z-30 flex items-center justify-center bg-slate-950/70 p-3 backdrop-blur-sm sm:p-6"
          onMouseDown={() => setSelectedEntry(null)}
          role="presentation"
        >
          <section
            className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-white/20 bg-white shadow-2xl"
            onMouseDown={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="metas-ul-history-detail-title"
          >
            <header className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 px-6 py-5 text-white sm:px-7">
              <div className="pointer-events-none absolute -right-12 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
              <div className="relative flex items-start justify-between gap-4">
                <div>
                  <span className="mb-2 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-blue-50">Detalhes da auditoria</span>
                  <h3 id="metas-ul-history-detail-title" className="text-xl font-bold text-white">Altera&ccedil;&otilde;es realizadas</h3>
                  <p className="mt-1 text-sm text-blue-100">{selectedDate.full}</p>
                </div>
                <button type="button" onClick={() => setSelectedEntry(null)} className="rounded-xl border border-white/10 bg-white/10 p-2.5 text-white/80 transition hover:bg-white/20 hover:text-white" aria-label="Fechar detalhes">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </header>

            <div className="grid grid-cols-1 gap-3 border-b border-slate-200 bg-slate-50 px-6 py-4 sm:grid-cols-2 sm:px-7">
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700"><UserRound className="h-5 w-5" /></div>
                <div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-900">{selectedEntry.name}</p><p className="truncate text-xs text-slate-500">{selectedEntry.email}</p></div>
              </div>
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700"><History className="h-5 w-5" /></div>
                <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total neste registro</p><p className="text-sm font-bold text-slate-900">{selectedData.changes?.length || 0} altera&ccedil;&otilde;es</p></div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-white p-4 sm:p-7">
              <div className="space-y-3">
                {(selectedData.changes || []).map((change, index) => (
                  <div key={`${change.bu}-${change.mes}-${index}`} className="grid grid-cols-1 items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-[110px_130px_minmax(0,1fr)_40px_minmax(0,1fr)]">
                    <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">BU</p><span className="mt-1 inline-flex rounded-lg bg-blue-700 px-2.5 py-1 text-xs font-bold text-white">{change.bu}</span></div>
                    <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">M&ecirc;s</p><p className="mt-1 text-sm font-semibold text-slate-800">{change.mesLabel}</p></div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Valor anterior</p><p className="mt-1 text-sm font-bold text-slate-700">{formatApprovedValue(change.antes)}</p></div>
                    <div className="hidden h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500 sm:flex"><ArrowRight className="h-4 w-4" /></div>
                    <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3"><p className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">Novo valor</p><p className="mt-1 text-sm font-bold text-blue-700">{formatApprovedValue(change.depois)}</p></div>
                  </div>
                ))}
              </div>
            </div>

            <footer className="flex justify-end border-t border-slate-200 bg-slate-50 px-6 py-4 sm:px-7">
              <button type="button" onClick={() => setSelectedEntry(null)} className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700">Fechar detalhes</button>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
};
